import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Dog, Cat, Calendar, Scale, Activity, Heart, AlertCircle, CheckCircle2, ArrowRight, ArrowLeft, Sparkles } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Progress } from '../ui/Progress';
interface PetProfile {
  name: string;
  type: 'dog' | 'cat' | null;
  breed: string;
  age: string;
  weight: string;
  activityLevel: 'low' | 'medium' | 'high' | null;
  healthConditions: string[];
  allergies: string[];
}
export function PetProfileBuilder() {
  const [step, setStep] = useState(1);
  const [profile, setProfile] = useState<PetProfile>({
    name: '',
    type: null,
    breed: '',
    age: '',
    weight: '',
    activityLevel: null,
    healthConditions: [],
    allergies: []
  });
  const totalSteps = 5;
  const progress = step / totalSteps * 100;
  const commonHealthConditions = ['Sobrepeso', 'Diabetes', 'Problemas renales', 'Problemas digestivos', 'Artritis', 'Alergias cutáneas'];
  const commonAllergies = ['Pollo', 'Ternera', 'Lácteos', 'Cereales', 'Soja', 'Pescado'];
  const toggleHealthCondition = (condition: string) => {
    setProfile(prev => ({
      ...prev,
      healthConditions: prev.healthConditions.includes(condition) ? prev.healthConditions.filter(c => c !== condition) : [...prev.healthConditions, condition]
    }));
  };
  const toggleAllergy = (allergy: string) => {
    setProfile(prev => ({
      ...prev,
      allergies: prev.allergies.includes(allergy) ? prev.allergies.filter(a => a !== allergy) : [...prev.allergies, allergy]
    }));
  };
  const canProceed = () => {
    switch (step) {
      case 1:
        return profile.name && profile.type;
      case 2:
        return profile.breed && profile.age;
      case 3:
        return profile.weight && profile.activityLevel;
      case 4:
        return true;
      // Health conditions are optional
      case 5:
        return true;
      // Allergies are optional
      default:
        return false;
    }
  };
  const handleComplete = () => {
    // Here you would save the profile
    alert('¡Perfil creado con éxito!');
  };
  return <div className="max-w-3xl mx-auto" data-id="element-5031">
      <Card className="overflow-hidden border-none shadow-xl" data-id="element-5032">
        {/* Header with Progress */}
        <div className="bg-gradient-to-r from-primary-600 to-primary-700 p-8 text-white" data-id="element-5033">
          <div className="flex items-center justify-between mb-4" data-id="element-5034">
            <Badge variant="outline" className="bg-white/10 border-white/20 text-white" data-id="element-5035">
              Paso {step} de {totalSteps}
            </Badge>
            <span className="text-sm font-medium opacity-90" data-id="element-5036">
              {Math.round(progress)}% completado
            </span>
          </div>
          <Progress value={progress} className="h-2 bg-white/20" data-id="element-5037" />
        </div>

        {/* Content */}
        <div className="p-8 md:p-12" data-id="element-5038">
          <AnimatePresence mode="wait" data-id="element-5039">
            {/* Step 1: Basic Info */}
            {step === 1 && <motion.div key="step1" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="space-y-8" data-id="element-5040">
                <div className="text-center mb-8" data-id="element-5041">
                  <h2 className="text-3xl font-bold text-charcoal-900 mb-3 font-serif" data-id="element-5042">
                    Conoce a tu mascota
                  </h2>
                  <p className="text-gray-600" data-id="element-5043">
                    Empecemos con lo básico para crear su perfil nutricional
                  </p>
                </div>

                <Input label="¿Cómo se llama?" placeholder="Ej: Max, Luna, Rocky..." value={profile.name} onChange={e => setProfile({
              ...profile,
              name: e.target.value
            })} leftIcon={<Heart className="w-5 h-5" data-id="element-5045" />} className="text-lg" data-id="element-5044" />

                <div data-id="element-5046">
                  <label className="block text-sm font-bold text-charcoal-700 mb-4" data-id="element-5047">
                    ¿Es perro o gato?
                  </label>
                  <div className="grid grid-cols-2 gap-4" data-id="element-5048">
                    <button onClick={() => setProfile({
                  ...profile,
                  type: 'dog'
                })} className={`p-6 rounded-2xl border-2 transition-all flex flex-col items-center gap-3 ${profile.type === 'dog' ? 'border-primary-600 bg-primary-50 text-primary-800' : 'border-gray-200 hover:border-primary-300 text-gray-600'}`} data-id="element-5049">
                      <Dog className="w-12 h-12" data-id="element-5050" />
                      <span className="font-bold text-lg" data-id="element-5051">Perro</span>
                    </button>
                    <button onClick={() => setProfile({
                  ...profile,
                  type: 'cat'
                })} className={`p-6 rounded-2xl border-2 transition-all flex flex-col items-center gap-3 ${profile.type === 'cat' ? 'border-primary-600 bg-primary-50 text-primary-800' : 'border-gray-200 hover:border-primary-300 text-gray-600'}`} data-id="element-5052">
                      <Cat className="w-12 h-12" data-id="element-5053" />
                      <span className="font-bold text-lg" data-id="element-5054">Gato</span>
                    </button>
                  </div>
                </div>
              </motion.div>}

            {/* Step 2: Breed & Age */}
            {step === 2 && <motion.div key="step2" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="space-y-8" data-id="element-5055">
                <div className="text-center mb-8" data-id="element-5056">
                  <h2 className="text-3xl font-bold text-charcoal-900 mb-3 font-serif" data-id="element-5057">
                    Detalles de {profile.name}
                  </h2>
                  <p className="text-gray-600" data-id="element-5058">
                    Esto nos ayuda a personalizar su nutrición
                  </p>
                </div>

                <Input label="Raza" placeholder="Ej: Labrador, Persa, Mestizo..." value={profile.breed} onChange={e => setProfile({
              ...profile,
              breed: e.target.value
            })} className="text-lg" data-id="element-5059" />

                <Input label="Edad (años)" type="number" placeholder="Ej: 3" value={profile.age} onChange={e => setProfile({
              ...profile,
              age: e.target.value
            })} leftIcon={<Calendar className="w-5 h-5" data-id="element-5061" />} className="text-lg" data-id="element-5060" />
              </motion.div>}

            {/* Step 3: Weight & Activity */}
            {step === 3 && <motion.div key="step3" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="space-y-8" data-id="element-5062">
                <div className="text-center mb-8" data-id="element-5063">
                  <h2 className="text-3xl font-bold text-charcoal-900 mb-3 font-serif" data-id="element-5064">
                    Físico y actividad
                  </h2>
                  <p className="text-gray-600" data-id="element-5065">
                    Para calcular las porciones perfectas
                  </p>
                </div>

                <Input label="Peso actual (kg)" type="number" placeholder="Ej: 15" value={profile.weight} onChange={e => setProfile({
              ...profile,
              weight: e.target.value
            })} leftIcon={<Scale className="w-5 h-5" data-id="element-5067" />} className="text-lg" data-id="element-5066" />

                <div data-id="element-5068">
                  <label className="block text-sm font-bold text-charcoal-700 mb-4" data-id="element-5069">
                    Nivel de actividad
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-5070">
                    {[{
                  value: 'low',
                  label: 'Baja',
                  desc: 'Paseos cortos',
                  icon: '🛋️'
                }, {
                  value: 'medium',
                  label: 'Media',
                  desc: '1-2 paseos/día',
                  icon: '🚶'
                }, {
                  value: 'high',
                  label: 'Alta',
                  desc: 'Muy activo',
                  icon: '🏃'
                }].map(level => <button key={level.value} onClick={() => setProfile({
                  ...profile,
                  activityLevel: level.value as any
                })} className={`p-4 rounded-xl border-2 transition-all text-left ${profile.activityLevel === level.value ? 'border-primary-600 bg-primary-50' : 'border-gray-200 hover:border-primary-300'}`} data-id="element-5071">
                        <div className="text-2xl mb-2" data-id="element-5072">{level.icon}</div>
                        <div className="font-bold text-charcoal-900" data-id="element-5073">
                          {level.label}
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-5074">
                          {level.desc}
                        </div>
                      </button>)}
                  </div>
                </div>
              </motion.div>}

            {/* Step 4: Health Conditions */}
            {step === 4 && <motion.div key="step4" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="space-y-8" data-id="element-5075">
                <div className="text-center mb-8" data-id="element-5076">
                  <h2 className="text-3xl font-bold text-charcoal-900 mb-3 font-serif" data-id="element-5077">
                    Condiciones de salud
                  </h2>
                  <p className="text-gray-600" data-id="element-5078">
                    Opcional, pero nos ayuda a adaptar la dieta
                  </p>
                </div>

                <div className="flex flex-wrap gap-3" data-id="element-5079">
                  {commonHealthConditions.map(condition => <button key={condition} onClick={() => toggleHealthCondition(condition)} className={`px-4 py-2 rounded-xl border-2 transition-all font-medium ${profile.healthConditions.includes(condition) ? 'border-primary-600 bg-primary-50 text-primary-800' : 'border-gray-200 hover:border-primary-300 text-gray-600'}`} data-id="element-5080">
                      {condition}
                    </button>)}
                </div>

                <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 flex gap-3" data-id="element-5081">
                  <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-5082" />
                  <p className="text-sm text-blue-900" data-id="element-5083">
                    Si tu mascota tiene condiciones médicas específicas,
                    consulta con tu veterinario antes de cambiar su dieta.
                  </p>
                </div>
              </motion.div>}

            {/* Step 5: Allergies */}
            {step === 5 && <motion.div key="step5" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="space-y-8" data-id="element-5084">
                <div className="text-center mb-8" data-id="element-5085">
                  <h2 className="text-3xl font-bold text-charcoal-900 mb-3 font-serif" data-id="element-5086">
                    Alergias o intolerancias
                  </h2>
                  <p className="text-gray-600" data-id="element-5087">
                    Excluiremos estos ingredientes de las recomendaciones
                  </p>
                </div>

                <div className="flex flex-wrap gap-3" data-id="element-5088">
                  {commonAllergies.map(allergy => <button key={allergy} onClick={() => toggleAllergy(allergy)} className={`px-4 py-2 rounded-xl border-2 transition-all font-medium ${profile.allergies.includes(allergy) ? 'border-red-600 bg-red-50 text-red-800' : 'border-gray-200 hover:border-red-300 text-gray-600'}`} data-id="element-5089">
                      {allergy}
                    </button>)}
                </div>

                <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-6 text-center" data-id="element-5090">
                  <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-4" data-id="element-5091" />
                  <h3 className="font-bold text-charcoal-900 text-lg mb-2" data-id="element-5092">
                    ¡Perfil casi completo!
                  </h3>
                  <p className="text-sm text-gray-600" data-id="element-5093">
                    Haz clic en "Finalizar" para ver las recomendaciones
                    personalizadas para {profile.name}
                  </p>
                </div>
              </motion.div>}
          </AnimatePresence>

          {/* Navigation */}
          <div className="flex gap-4 mt-12 pt-8 border-t border-gray-100" data-id="element-5094">
            {step > 1 && <Button variant="outline" onClick={() => setStep(step - 1)} className="flex-1" data-id="element-5095">
                <ArrowLeft className="w-4 h-4 mr-2" data-id="element-5096" />
                Atrás
              </Button>}
            {step < totalSteps ? <Button onClick={() => setStep(step + 1)} disabled={!canProceed()} className="flex-1" data-id="element-5097">
                Continuar
                <ArrowRight className="w-4 h-4 ml-2" data-id="element-5098" />
              </Button> : <Button onClick={handleComplete} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-id="element-5099">
                <Sparkles className="w-4 h-4 mr-2" data-id="element-5100" />
                Finalizar Perfil
              </Button>}
          </div>
        </div>
      </Card>
    </div>;
}